import React, { useState, useEffect, useCallback } from "react";
import { Plus, Edit, Trash2, Search, RefreshCw, Building2, CreditCard, MapPin, MoreVertical, Layers } from "lucide-react";
import billingService from "../../services/billingService";
import { paymentService } from "../../services/paymentService";
import { showApiMessage } from "../../utils/messageHandler";
import { Button } from "../../shadcn/ui/button";
import { Input } from "../../shadcn/ui/input";
import { Label } from "../../shadcn/ui/label";
import { Textarea } from "../../shadcn/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../shadcn/ui/card";
import { Badge } from "../../shadcn/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../shadcn/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../shadcn/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../shadcn/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../shadcn/ui/select";
import { Checkbox } from "../../shadcn/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../shadcn/ui/dropdown-menu";
import { useCountries } from "../../hooks/useCountries";
import logger from "../../utils/logger";


const PaymentConfiguration = () => {
  const { countries, countriesLoading } = useCountries();
  const [activeTab, setActiveTab] = useState("institutions");
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  // Data states
  const [paymentInstitutions, setPaymentInstitutions] = useState([]);
  const [paymentChannels, setPaymentChannels] = useState([]);
  const [institutionBranches, setInstitutionBranches] = useState([]);
  const [institutionTypes, setInstitutionTypes] = useState([]);

  // Form states
  const [showInstitutionForm, setShowInstitutionForm] = useState(false);
  const [showChannelForm, setShowChannelForm] = useState(false);
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [showTypeForm, setShowTypeForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Institution form data
  const [institutionForm, setInstitutionForm] = useState({
    shortName: "",
    name: "",
    active: true,
    countryId: 119, // Default to Kenya
    paymentChannelType: "BANK",
    code: "",
    swiftCode: "",
  });

  // Channel form data
  const [channelForm, setChannelForm] = useState({
    countryId: 119,
    code: "",
    name: "",
    description: "",
    chargeType: "PERCENT",
    chargeRate: 0,
    minimumCharge: 0,
    maximumCharge: 0,
    applyTax: false,
    hasThirdPartyApiSupport: false,
  });

  // Branch form data
  const [branchForm, setBranchForm] = useState({
    paymentInstitutionId: "",
    name: "",
    sortCode: "",
    description: "",
  });

  // Institution Type form data
  const [typeForm, setTypeForm] = useState({
    name: "",
    code: "",
    countryCode: "KE",
    description: "",
  });

  const tabs = [
    {
      id: "institutions",
      label: "Payment Institutions",
      count: paymentInstitutions.length,
      icon: Building2,
      description: "Manage banks and financial institutions",
    },
    {
      id: "channels",
      label: "Payment Channels",
      count: paymentChannels.length,
      icon: CreditCard,
      description: "Configure payment processing channels",
    },
    {
      id: "branches",
      label: "Institution Branches",
      count: institutionBranches.length,
      icon: MapPin,
      description: "Manage bank branches and locations",
    },
    {
      id: "types",
      label: "Institution Types",
      count: institutionTypes.length,
      icon: Layers,
      description: "Manage payment institution types (nuban, ghipss, etc.)",
    },
  ];

  // Fetch data based on active tab
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      switch (activeTab) {
        case "institutions":
          const institutionsResponse = await billingService.getPaymentInstitutions({
            limit: 100,
            ...(filterType !== "all" && { paymentChannelType: filterType }),
          });
          setPaymentInstitutions(institutionsResponse.data.data || []);
          break;
        case "channels":
          const channelsResponse = await billingService.getPaymentChannels({
            limit: 100,
            ...(filterType !== "all" && { chargeType: filterType }),
          });
          setPaymentChannels(channelsResponse.data.data || []);
          break;
        case "branches":
          const branchesResponse = await billingService.getPaymentInstitutionBranches({
            limit: 100,
          });
          setInstitutionBranches(branchesResponse.data.data || []);
          break;
        case "types":
          const typesResponse = await paymentService.getPaymentInstitutionTypes({
            limit: 100,
          });
          setInstitutionTypes(typesResponse.data?.data || typesResponse.data || []);
          break;
        default:
          break;
      }
    } catch (error) {
      logger.error(`Error fetching ${activeTab}:`, error);
      showApiMessage(`Failed to load ${activeTab}`, "error");
    } finally {
      setLoading(false);
    }
  }, [activeTab, filterType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle form submissions
  const handleInstitutionSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingItem) {
        // For update, exclude countryId as it's not in the PATCH schema
        const { countryId, ...updatePayload } = institutionForm;
        await billingService.updatePaymentInstitution(editingItem.id, updatePayload);
        showApiMessage("Payment institution updated successfully", "success");
      } else {
        // For create, include countryId as it's required
        await billingService.createPaymentInstitution(institutionForm);
        showApiMessage("Payment institution created successfully", "success");
      }
      setShowInstitutionForm(false);
      setEditingItem(null);
      setInstitutionForm({
        shortName: "",
        name: "",
        active: true,
        countryId: 119,
        paymentChannelType: "BANK",
        code: "",
        swiftCode: "",
      });
      fetchData();
    } catch (error) {
      logger.error("Error saving institution:", error);
      showApiMessage(error, "save payment institution");
    } finally {
      setLoading(false);
    }
  };

  const handleChannelSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingItem) {
        await billingService.updatePaymentChannel(editingItem.id, channelForm);
        showApiMessage("Payment channel updated successfully", "success");
      } else {
        await billingService.createPaymentChannel(channelForm);
        showApiMessage("Payment channel created successfully", "success");
      }
      setShowChannelForm(false);
      setEditingItem(null);
      setChannelForm({
        countryId: 119,
        code: "",
        name: "",
        description: "",
        chargeType: "PERCENT",
        chargeRate: 0,
        minimumCharge: 0,
        maximumCharge: 0,
        applyTax: false,
        hasThirdPartyApiSupport: false,
      });
      fetchData();
    } catch (error) {
      logger.error("Error saving channel:", error);
      showApiMessage(error, "save payment channel");
    } finally {
      setLoading(false);
    }
  };

  const handleBranchSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingItem) {
        await billingService.updatePaymentInstitutionBranch(editingItem.id, branchForm);
        showApiMessage("Institution branch updated successfully", "success");
      } else {
        await billingService.createPaymentInstitutionBranch(branchForm);
        showApiMessage("Institution branch created successfully", "success");
      }
      setShowBranchForm(false);
      setEditingItem(null);
      setBranchForm({
        paymentInstitutionId: "",
        name: "",
        sortCode: "",
        description: "",
      });
      fetchData();
    } catch (error) {
      logger.error("Error saving branch:", error);
      showApiMessage(error, "save institution branch");
    } finally {
      setLoading(false);
    }
  };

  const handleTypeSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingItem) {
        await paymentService.updatePaymentInstitutionType(editingItem.id, typeForm);
        showApiMessage("Institution type updated successfully", "success");
      } else {
        await paymentService.createPaymentInstitutionType(typeForm);
        showApiMessage("Institution type created successfully", "success");
      }
      setShowTypeForm(false);
      setEditingItem(null);
      setTypeForm({
        name: "",
        code: "",
        countryCode: "KE",
        description: "",
      });
      fetchData();
    } catch (error) {
      logger.error("Error saving institution type:", error);
      showApiMessage(error, "save institution type");
    } finally {
      setLoading(false);
    }
  };

  // Handle delete operations
  const handleDelete = async (item, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;

    setLoading(true);
    try {
      switch (type) {
        case "institution":
          await billingService.deletePaymentInstitution(item.id);
          break;
        case "channel":
          await billingService.deletePaymentChannel(item.id);
          break;
        case "branch":
          await billingService.deletePaymentInstitutionBranch(item.id);
          break;
        case "type":
          await paymentService.deletePaymentInstitutionType(item.id);
          break;
        default:
          break;
      }
      showApiMessage(`${type} deleted successfully`, "success");
      fetchData();
    } catch (error) {
      logger.error(`Error deleting ${type}:`, error);
      showApiMessage(`Failed to delete ${type}`, "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle edit operations
  const handleEdit = (item, type) => {
    setEditingItem(item);
    switch (type) {
      case "institution":
        // Only include allowed fields for institution update (exclude id, countryId, createdAt, modifiedAt, country)
        setInstitutionForm({
          shortName: item.shortName || "",
          name: item.name || "",
          active: item.active ?? true,
          paymentChannelType: item.paymentChannelType || "BANK",
          code: item.code || "",
          swiftCode: item.swiftCode || "",
        });
        setShowInstitutionForm(true);
        break;
      case "channel":
        // Only include allowed fields for channel update
        setChannelForm({
          code: item.code || "",
          name: item.name || "",
          description: item.description || "",
          chargeType: item.chargeType || "PERCENT",
          chargeRate: item.chargeRate || 0,
          minimumCharge: item.minimumCharge || 0,
          maximumCharge: item.maximumCharge || 0,
          applyTax: item.applyTax ?? false,
          hasThirdPartyApiSupport: item.hasThirdPartyApiSupport ?? false,
        });
        setShowChannelForm(true);
        break;
      case "branch":
        // Only include allowed fields for branch update
        setBranchForm({
          paymentInstitutionId: item.paymentInstitutionId || "",
          name: item.name || "",
          sortCode: item.sortCode || "",
          description: item.description || "",
        });
        setShowBranchForm(true);
        break;
      case "type":
        setTypeForm({
          name: item.name || "",
          code: item.code || "",
          countryCode: item.countryCode || "KE",
          description: item.description || "",
        });
        setShowTypeForm(true);
        break;
      default:
        break;
    }
  };

  // Filter data based on search term
  const filteredData = () => {
    let data = [];
    switch (activeTab) {
      case "institutions":
        data = paymentInstitutions;
        break;
      case "channels":
        data = paymentChannels;
        break;
      case "branches":
        data = institutionBranches;
        break;
      case "types":
        data = institutionTypes;
        break;
      default:
        data = [];
        break;
    }

    if (searchTerm) {
      data = data.filter(
        item =>
          item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.shortName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.countryCode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return data;
  };

  const renderInstitutionForm = () => (
    <Dialog open={showInstitutionForm} onOpenChange={setShowInstitutionForm}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editingItem ? "Edit" : "Add"} Payment Institution</DialogTitle>
          <DialogDescription>
            {editingItem ? "Update the payment institution details." : "Create a new payment institution."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleInstitutionSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="shortName">Short Name *</Label>
              <Input
                id="shortName"
                type="text"
                required
                value={institutionForm.shortName}
                onChange={e =>
                  setInstitutionForm({
                    ...institutionForm,
                    shortName: e.target.value,
                  })
                }
                placeholder="e.g., KCB"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                type="text"
                required
                value={institutionForm.code}
                onChange={e =>
                  setInstitutionForm({
                    ...institutionForm,
                    code: e.target.value,
                  })
                }
                placeholder="e.g., 001"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Full Name *</Label>
            <Input
              id="name"
              type="text"
              required
              value={institutionForm.name}
              onChange={e => setInstitutionForm({ ...institutionForm, name: e.target.value })}
              placeholder="e.g., Kenya Commercial Bank"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="swiftCode">SWIFT Code</Label>
            <Input
              id="swiftCode"
              type="text"
              value={institutionForm.swiftCode}
              onChange={e =>
                setInstitutionForm({
                  ...institutionForm,
                  swiftCode: e.target.value,
                })
              }
              placeholder="e.g., KCBLKENX"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="country">Country *</Label>
            <Select
              value={institutionForm.countryId?.toString()}
              onValueChange={value =>
                setInstitutionForm({
                  ...institutionForm,
                  countryId: parseInt(value),
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {countriesLoading ? (
                  <SelectItem value="loading" disabled>
                    Loading countries...
                  </SelectItem>
                ) : (
                  countries.map(country => (
                    <SelectItem key={country.id} value={country.id.toString()}>
                      {country.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="type">Type *</Label>
            <Select
              value={institutionForm.paymentChannelType}
              onValueChange={value =>
                setInstitutionForm({
                  ...institutionForm,
                  paymentChannelType: value,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BANK">Bank</SelectItem>
                <SelectItem value="WALLET">Wallet</SelectItem>
                <SelectItem value="OTHER">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="active"
              checked={institutionForm.active}
              onCheckedChange={checked => setInstitutionForm({ ...institutionForm, active: checked })}
            />
            <Label htmlFor="active" className="text-sm font-medium">
              Active
            </Label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowInstitutionForm(false);
                setEditingItem(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : editingItem ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const renderChannelForm = () => (
    <Dialog open={showChannelForm} onOpenChange={setShowChannelForm}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingItem ? "Edit" : "Add"} Payment Channel</DialogTitle>
          <DialogDescription>
            {editingItem ? "Update the payment channel configuration." : "Create a new payment processing channel."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleChannelSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="channelName">Name *</Label>
              <Input
                id="channelName"
                type="text"
                required
                value={channelForm.name}
                onChange={e => setChannelForm({ ...channelForm, name: e.target.value })}
                placeholder="e.g., M-Pesa"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="channelCode">Code *</Label>
              <Input
                id="channelCode"
                type="text"
                required
                value={channelForm.code}
                onChange={e => setChannelForm({ ...channelForm, code: e.target.value })}
                placeholder="e.g., MPESA"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={channelForm.description}
              onChange={e => setChannelForm({ ...channelForm, description: e.target.value })}
              placeholder="Enter channel description..."
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="channelCountry">Country *</Label>
            <Select
              value={channelForm.countryId?.toString()}
              onValueChange={value =>
                setChannelForm({
                  ...channelForm,
                  countryId: parseInt(value),
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {countriesLoading ? (
                  <SelectItem value="loading" disabled>
                    Loading countries...
                  </SelectItem>
                ) : (
                  countries.map(country => (
                    <SelectItem key={country.id} value={country.id.toString()}>
                      {country.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="chargeType">Charge Type *</Label>
              <Select
                value={channelForm.chargeType}
                onValueChange={value => setChannelForm({ ...channelForm, chargeType: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select charge type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERCENT">Percentage</SelectItem>
                  <SelectItem value="FLAT">Flat Rate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="chargeRate">Charge Rate *</Label>
              <Input
                id="chargeRate"
                type="number"
                required
                step="0.01"
                value={channelForm.chargeRate}
                onChange={e =>
                  setChannelForm({
                    ...channelForm,
                    chargeRate: parseFloat(e.target.value),
                  })
                }
                placeholder="0.00"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="minCharge">Min Charge</Label>
              <Input
                id="minCharge"
                type="number"
                step="0.01"
                value={channelForm.minimumCharge}
                onChange={e =>
                  setChannelForm({
                    ...channelForm,
                    minimumCharge: parseFloat(e.target.value),
                  })
                }
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="maxCharge">Max Charge</Label>
              <Input
                id="maxCharge"
                type="number"
                step="0.01"
                value={channelForm.maximumCharge}
                onChange={e =>
                  setChannelForm({
                    ...channelForm,
                    maximumCharge: parseFloat(e.target.value),
                  })
                }
                placeholder="0.00"
              />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="applyTax"
                checked={channelForm.applyTax}
                onCheckedChange={checked => setChannelForm({ ...channelForm, applyTax: checked })}
              />
              <Label htmlFor="applyTax" className="text-sm font-medium">
                Apply Tax
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="hasThirdPartyApiSupport"
                checked={channelForm.hasThirdPartyApiSupport}
                onCheckedChange={checked =>
                  setChannelForm({
                    ...channelForm,
                    hasThirdPartyApiSupport: checked,
                  })
                }
              />
              <Label htmlFor="hasThirdPartyApiSupport" className="text-sm font-medium">
                Third Party API Support
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowChannelForm(false);
                setEditingItem(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : editingItem ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const renderBranchForm = () => (
    <Dialog open={showBranchForm} onOpenChange={setShowBranchForm}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editingItem ? "Edit" : "Add"} Institution Branch</DialogTitle>
          <DialogDescription>
            {editingItem ? "Update the branch details." : "Create a new institution branch."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleBranchSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="institution">Institution *</Label>
            <Select
              value={branchForm.paymentInstitutionId.toString()}
              onValueChange={value =>
                setBranchForm({
                  ...branchForm,
                  paymentInstitutionId: parseInt(value),
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Institution" />
              </SelectTrigger>
              <SelectContent>
                {paymentInstitutions.map(inst => (
                  <SelectItem key={inst.id} value={inst.id.toString()}>
                    {inst.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="branchName">Branch Name *</Label>
              <Input
                id="branchName"
                type="text"
                required
                value={branchForm.name}
                onChange={e => setBranchForm({ ...branchForm, name: e.target.value })}
                placeholder="e.g., Westlands Branch"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sortCode">Sort Code *</Label>
              <Input
                id="sortCode"
                type="text"
                required
                value={branchForm.sortCode}
                onChange={e => setBranchForm({ ...branchForm, sortCode: e.target.value })}
                placeholder="e.g., 001"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="branchDescription">Description</Label>
            <Textarea
              id="branchDescription"
              value={branchForm.description}
              onChange={e => setBranchForm({ ...branchForm, description: e.target.value })}
              placeholder="Enter branch description..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowBranchForm(false);
                setEditingItem(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : editingItem ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const renderTypeForm = () => (
    <Dialog open={showTypeForm} onOpenChange={setShowTypeForm}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editingItem ? "Edit" : "Add"} Institution Type</DialogTitle>
          <DialogDescription>
            {editingItem ? "Update the payment institution type details." : "Create a new payment institution type."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleTypeSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="typeName">Name *</Label>
              <Input
                id="typeName"
                type="text"
                required
                value={typeForm.name}
                onChange={e => setTypeForm({ ...typeForm, name: e.target.value })}
                placeholder="e.g., Nigerian Uniform Bank Account Number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="typeCode">Code *</Label>
              <Select
                value={typeForm.code}
                onValueChange={value => setTypeForm({ ...typeForm, code: value })}
              >
                <SelectTrigger id="typeCode">
                  <SelectValue placeholder="Select type code" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nuban">nuban (Nigeria / Standard Bank)</SelectItem>
                  <SelectItem value="ghipss">ghipss (Ghana)</SelectItem>
                  <SelectItem value="mobile_money">mobile_money (Mobile Money / Wallets)</SelectItem>
                  <SelectItem value="basa">basa (South Africa)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="typeCountryCode">Country</Label>
            <Select
              value={typeForm.countryCode || "KE"}
              onValueChange={value => setTypeForm({ ...typeForm, countryCode: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {countriesLoading ? (
                  <SelectItem value="loading" disabled>
                    Loading countries...
                  </SelectItem>
                ) : (
                  countries.map(country => (
                    <SelectItem key={country.id} value={country.iso_code || country.code || country.name}>
                      {country.name} ({country.iso_code || country.code})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="typeDescription">Description</Label>
            <Textarea
              id="typeDescription"
              value={typeForm.description}
              onChange={e => setTypeForm({ ...typeForm, description: e.target.value })}
              placeholder="Enter type description..."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowTypeForm(false);
                setEditingItem(null);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : editingItem ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const renderTable = () => {
    const data = filteredData();

    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      );
    }

    if (data.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">No {activeTab} found</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {activeTab === "institutions" && (
                <>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>SWIFT</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </>
              )}
              {activeTab === "channels" && (
                <>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Charge Type</TableHead>
                  <TableHead>Rate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </>
              )}
              {activeTab === "branches" && (
                <>
                  <TableHead>Institution</TableHead>
                  <TableHead>Branch Name</TableHead>
                  <TableHead>Sort Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </>
              )}
              {activeTab === "types" && (
                <>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map(item => (
              <TableRow key={item.id} className="hover:bg-gray-50">
                {activeTab === "institutions" && (
                  <>
                    <TableCell>
                      <div>
                        <div className="font-medium">{item.name}</div>
                        <div className="text-sm text-gray-500">{item.shortName}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.code}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{item.paymentChannelType}</Badge>
                    </TableCell>
                    <TableCell>{item.swiftCode || "-"}</TableCell>
                    <TableCell>
                      <Badge variant={item.active ? "default" : "destructive"}>
                        {item.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(item, "institution")}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(item, "institution")} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </>
                )}
                {activeTab === "channels" && (
                  <>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.code}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{item.chargeType}</Badge>
                    </TableCell>
                    <TableCell>{item.chargeRate}%</TableCell>
                    <TableCell>
                      <Badge variant={item.active ? "default" : "destructive"}>
                        {item.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(item, "channel")}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(item, "channel")} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </>
                )}
                {activeTab === "branches" && (
                  <>
                    <TableCell>
                      {paymentInstitutions.find(inst => inst.id === item.paymentInstitutionId)?.name || "Unknown"}
                    </TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.sortCode}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={item.active ? "default" : "destructive"}>
                        {item.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(item, "branch")}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(item, "branch")} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </>
                )}
                {activeTab === "types" && (
                  <>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.code}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{item.countryCode || "-"}</Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-gray-500">{item.description || "-"}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(item, "type")}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(item, "type")} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Payment Configuration</h1>
        <p className="text-gray-600 mt-2">Manage payment institutions, channels, and branches</p>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        {/* Tabs Navigation */}
        <TabsList className="inline-flex w-full sm:w-auto h-auto p-1.5 gap-1.5 flex-wrap sm:flex-nowrap bg-muted rounded-xl">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm">
                <Icon className="w-4 h-4 shrink-0" />
                <span className="whitespace-nowrap">{tab.label}</span>
                <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                  {tab.count}
                </Badge>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Tab Content */}
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <TabsContent key={tab.id} value={tab.id} className="space-y-6">
              {/* Tab Header */}
              <Card>
                <CardHeader>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <CardTitle className="text-xl">{tab.label}</CardTitle>
                        <CardDescription>{tab.description}</CardDescription>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Button
                        onClick={() => {
                          setEditingItem(null);
                          if (tab.id === "institutions") setShowInstitutionForm(true);
                          else if (tab.id === "channels") setShowChannelForm(true);
                          else if (tab.id === "branches") setShowBranchForm(true);
                          else if (tab.id === "types") setShowTypeForm(true);
                        }}
                        className="w-full sm:w-auto"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add {tab.id === "institutions" ? "Institution" : tab.id === "channels" ? "Channel" : tab.id === "branches" ? "Branch" : "Institution Type"}
                      </Button>
                      <Button onClick={fetchData} disabled={loading} variant="outline" className="w-full sm:w-auto">
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                      </Button>
                    </div>
                  </div>
                </CardHeader>
              </Card>

              {/* Search and Filter */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-1">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          type="text"
                          placeholder={`Search ${tab.label.toLowerCase()}...`}
                          value={searchTerm}
                          onChange={e => setSearchTerm(e.target.value)}
                          className="!pl-10"
                        />
                      </div>
                    </div>
                    {tab.id !== "branches" && tab.id !== "types" && (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Select value={filterType} onValueChange={setFilterType}>
                          <SelectTrigger className="w-full sm:w-[180px]">
                            <SelectValue placeholder="Filter by type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            {tab.id === "institutions" && (
                              <>
                                <SelectItem value="BANK">Bank</SelectItem>
                                <SelectItem value="WALLET">Wallet</SelectItem>
                                <SelectItem value="OTHER">Other</SelectItem>
                              </>
                            )}
                            {tab.id === "channels" && (
                              <>
                                <SelectItem value="PERCENT">Percentage</SelectItem>
                                <SelectItem value="FLAT">Flat Rate</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Data Table */}
              <Card>
                <CardContent className="p-0">{renderTable()}</CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Forms */}
      {renderInstitutionForm()}
      {renderChannelForm()}
      {renderBranchForm()}
      {renderTypeForm()}
    </div>
  );
};

export default PaymentConfiguration;
